import { type ReactNode, useCallback } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';
import { useLoginStateAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

import { useDeleteVideoModal } from './hooks/useDeleteVideoModal';
import type { PetitionVideoSurface, SupporterVideo } from './types';

type Result = ModelHookResult<
	{ canDeleteVideo: boolean; deleteVideoModal: ReactNode },
	{
		handleDeleteClick: () => Promise<void>;
	}
>;

export function useDeleteVideo({
	petitionId,
	video,
	renderedOn,
	afterDeleteAction,
}: {
	petitionId: string;
	video: SupporterVideo;
	renderedOn: PetitionVideoSurface;
	afterDeleteAction: (videoId?: string) => void;
}): Result {
	const navigate = useNavigate();
	const { track } = useCampaignTracking();
	const canDeleteVideo = video.viewerCanDelete;
	const { deleteVideoModal, openDeleteModal } = useDeleteVideoModal({ petitionId, afterDeleteAction, renderedOn });

	const location = useLocation();

	const loginStateBase = useLoginStateAsync();
	const loginState = isLoaded(loginStateBase) ? loginStateBase.value : undefined;

	const handleDeleteClick = useCallback(async () => {
		const trackingData: TrackingProperties = {
			petition_id: petitionId,
			page: renderedOn,
			video_id: video.id,
			video_creator_id: video.user?.id,
		};
		track('supporter_voice_delete_button_click', trackingData);
		if (loginState === 'IDENTIFIED') {
			navigate(`/login_or_join?redirectTo=${location.pathname}`);
		} else {
			openDeleteModal(video);
		}
	}, [location, loginState, navigate, openDeleteModal, petitionId, renderedOn, track, video]);

	return {
		data: {
			canDeleteVideo,
			deleteVideoModal,
		},
		actions: {
			handleDeleteClick,
		},
	};
}
